import './NotificationsSettings.css';

import { Button } from '@material-ui/core';
import React, { useEffect, useState, useMemo } from 'react';
import {
	confirmPushNotifications,
	rejectPushNotifications,
} from '../../stores/actions/dashboardActions';
import { createStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Card from '../../../components/Card/Card';
import CardBody from '../../../components/Card/CardBody';
import CardHeader from '../../../components/Card/CardHeader';
import CardIcon from '../../../components/Card/CardIcon';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import GridItem from '../../../components/Grid/GridItem';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Notifications from '@material-ui/icons/Notifications';
import Select from '@material-ui/core/Select';
import { closeNotificationSettingsUpdate } from '../../stores/actions/accountActions';
import { connect } from 'react-redux';
import utils from '../../helpers/GlobalVariables';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { Account, IDateObject } from '../../helpers/interfaces';

const useStyles = makeStyles((theme) =>
	createStyles({
		formControl: {
			margin: theme.spacing(1),
			minWidth: 120,
			width: '50%',
		},
		chips: {
			display: 'flex',
			flexWrap: 'wrap',
		},
		chip: {
			margin: 2,
		},
		noLabel: {
			marginTop: theme.spacing(3),
		},
		headerColor: { color: '#3C4858' },
	})
);

const MenuProps = {
	PaperProps: {
		style: {
			width: 250,
		},
	},
};
const getStyles = (name, personName, theme) => {
	return {
		fontWeight:
			personName.indexOf(name) === -1
				? theme.typography.fontWeightRegular
				: theme.typography.fontWeightMedium,
	};
};
const notificationGroups = ['System', 'Heartbeat', 'L1', 'L2', 'L3', 'L4', 'Obstruction'];

interface NotificationsSettingsProps {
	accountRole: string;
	systemID: number;
	systemName: string;
	editEnabled: boolean;
	notificationsUpdated: boolean;
	mobileView: boolean;
	emailProps: string[];
	pushProps: string[];
	smsProps: string[];
	getVisibleSystemsForAccountShort: (askedAccountId: number) => void;
	renderMobileNotificationsNotifications: () => void;
	askedAccountId: number;
	detachConfirmed: boolean;
	updatedSystemNotificationId: number;
	classes: ClassNameMap;
	account: Account;
	assignmentUpdatedAt: string,
	systemVersion: string;
	updateAssignmentByID: (
		accountID: number,
		systemID: number,
		accountRole: string,
		assignmentUpdatedAt: string,
		email: string[],
		push: string[],
		sms: string[]
	) => void;
}

const NotificationsSettings = ({
	accountRole,
	systemID,
	systemName,
	editEnabled,
	updateAssignmentByID,
	mobileView,
	emailProps,
	pushProps,
	smsProps,
	getVisibleSystemsForAccountShort,
	renderMobileNotificationsNotifications,
	askedAccountId,
	detachConfirmed,
	updatedSystemNotificationId,
	account,
	assignmentUpdatedAt,
	systemVersion,
	notificationsUpdated
}: NotificationsSettingsProps) => {
	const [email, selectEmail] = useState(emailProps);
	const [push, selectPush] = useState(pushProps);
	const [sms, selectSms] = useState(smsProps);
	const [buttonDisabled, setButtonDisabled] = useState(false);
	const classes = useStyles();
	const theme = useTheme();

	useEffect(() => {
		if (updatedSystemNotificationId === systemID) {
			if (detachConfirmed) {
				getVisibleSystemsForAccountShort(askedAccountId);
			}
		}
	}, [
		askedAccountId,
		detachConfirmed,
		getVisibleSystemsForAccountShort,
		systemID,
		updatedSystemNotificationId,
	]);

	useEffect(() => {
		setButtonDisabled(false);
	}, [notificationsUpdated]);

	const renderDropdown = (notificationsType, notiGroup, notiSetter) => {
		return (
			<FormControl className={classes.formControl}>
				<InputLabel id='push-multi-chip-label'>{notificationsType}</InputLabel>
				<Select
					disabled={!editEnabled || systemVersion === 'W2'}
					label='push-multi-chip-label'
					id='push-multi-chip'
					multiple
					value={notiGroup}
					onChange={(e) => notiSetter(e.target.value)}
					input={<Input id='select-multiple-chip' />}
					renderValue={(selected) => (
						<div className={classes.chips}>
							{(selected as string[]).map((value) => (
								<Chip key={value} label={value} className={classes.chip} />
							))}
						</div>
					)}
					MenuProps={MenuProps}
				>
					{notificationGroups.map((name, i) => (
						<MenuItem
							key={`name-${i}`}
							value={name}
							style={getStyles(name, notiGroup, theme)}
						>
							{name}
						</MenuItem>
					))}
				</Select>
			</FormControl>
		);
	};

	const renderButtons = () => {
		if (editEnabled) {
			return (
				<>
					<hr />
					<Box display='flex' alignItems='flex-start' justifyContent='space-between'>
						<Button onClick={handleSaveNotificationsSettings} disabled={buttonDisabled}>
							Save
						</Button>
					</Box>
				</>
			);
		}
	};

	const handleSaveNotificationsSettings = () => {
		if (!assignmentUpdatedAt) return null;
		setButtonDisabled(true);
		updateAssignmentByID(
			account.id,
			systemID,
			accountRole,
			assignmentUpdatedAt,
			email,
			push,
			sms
		);
	};

	const renderNotifySettings = (): JSX.Element => {
		if (systemVersion == 'W2') {
			return (<div >Visit Central Unit website for notifications settings.</div>);
		}
		return (
			<>
				<div style={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row' }}>
					{renderDropdown('Email', email, selectEmail)}
					{renderDropdown('Push', push, selectPush)}
					{renderDropdown('SMS', sms, selectSms)}
				</div>
				{renderMobileNotificationsNotifications()}
				{renderButtons()}
			</>
		);
	};
	const card = useMemo(() => {
	return (
		<GridItem xs={12} md={6}>
			<Card>
				<CardHeader color='info' icon>
					<CardIcon color='info'>
						<Notifications />
					</CardIcon>
					<h4 className={classes.headerColor}>
						Notifications settings for{' '}
						<span style={{ fontWeight: 500 }}>{systemName}</span>
					</h4>
				</CardHeader>
				<CardBody>{renderNotifySettings()}</CardBody>
			</Card>
		</GridItem>
	)}, [mobileView, pushProps, emailProps, smsProps, editEnabled, buttonDisabled, email, push, sms]);
	return card;
};

const mapStateToProps = (state) => {
	return {
		accountsWithPushNotificationsApproval: state.dashboard.accountsWithPushNotificationsApproval,
		detachConfirmed: state.account.detachConfirmed,
		account: state.dashboard.account,
		// mobileView: state.dashboard.mobileView
	};
};
const actions = {
	confirmPushNotifications,
	rejectPushNotifications,
	closeNotificationSettingsUpdate,
};

export default React.memo(connect(mapStateToProps, actions)(NotificationsSettings));
